import React from 'react'
import PropTypes from 'prop-types'
import MetaData from './MetaData'

/**
 * Enhanced MetaData component for racing line pages with OG image support
 *
 * This component automatically generates the appropriate OG image URL
 * for racing line pages based on the line data provided
 */
const RacingLineMetaData = ({ line, location }) => {
    // Generate OG image URL
    const ogImageUrl = generateRacingLineOGImageUrl(line)

    // Create title and description
    const title = `${line.trackName || 'Racing Line'} by ${line.userName || line.displayName || 'Racer'} | Open Racer`
    const description = generateRacingLineDescription(line)

    return (
        <MetaData
            title={title}
            description={description}
            image={ogImageUrl}
            location={location}
        />
    )
}

/**
 * Generate OG image URL for a racing line
 * Uses pre-generated static images from the build process
 */
function generateRacingLineOGImageUrl(line) {
    // Use static pre-generated OG images based on line ID or track name
    // Fall back to default if no specific image exists
    const lineId = line.id || line.lineId
    if (lineId) {
        return `/og-images/line-${lineId}.png`
    }

    // Fallback to track-based OG image or default
    const trackSlug = (line.trackName || 'race-track')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')

    return `/og-images/track-${trackSlug}.png`
}

/**
 * Format lap time for display (handles seconds or formatted strings)
 */
function formatLapTime(lapTime) {
    if (typeof lapTime === 'string') return lapTime

    if (typeof lapTime === 'number') {
        const minutes = Math.floor(lapTime / 60)
        const seconds = (lapTime % 60).toFixed(3)
        return `${minutes}:${seconds.padStart(6, '0')}`
    }

    return ''
}

/**
 * Generate a descriptive meta description for a racing line
 */
function generateRacingLineDescription(line) {
    const parts = []

    parts.push(`View the racing line for ${line.trackName || 'this track'}`)

    if (line.userName || line.displayName) {
        parts.push(`by ${line.userName || line.displayName}`)
    }

    if (line.lapTime) {
        parts.push(`| Lap time: ${formatLapTime(line.lapTime)}`)
    }

    if (line.vehicle) {
        parts.push(`| Vehicle: ${line.vehicle}`)
    }

    if (line.likes && line.likes > 0) {
        parts.push(`| ${line.likes} likes`)
    }

    parts.push('| Analyze telemetry data, braking points, and racing techniques on Open Racer.')

    return parts.join(' ')
}

RacingLineMetaData.propTypes = {
    line: PropTypes.shape({
        trackName: PropTypes.string,
        userName: PropTypes.string,
        displayName: PropTypes.string,
        lapTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        vehicle: PropTypes.string,
        likes: PropTypes.number,
    }).isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
    }).isRequired,
}

export default RacingLineMetaData
